import React, { Component } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import QueryString from 'query-string'

import Loader from '../../../../common/components/notifications/Loader'
import AudioDescriptionIcon from '../../../../common/components/AudioDescriptionIcon'
import config from '../../../../config'
import { getHasRequestPending } from '../../../../redux/data/selectors'

function getAdStudioUrl (event, project) {
    const baseUrl = ((config.apps && config.apps.audioDescriptionStudio) || 'https://ad.invintus.com').replace(/\/$/, '')

    // Existing project → deep-link to it. AD Studio loads the manifest from
    // storage; no need to repopulate context via query params.
    if (project && project.id) {
        return `${baseUrl}/projects/${project.id}`
    }

    // No project yet → bootstrap a new one from event context.
    const params = {
        clientID: event.clientID,
        eventID: event.eventID,
        source: 'controlcenter',
    }
    const query = QueryString.stringify(params)
    return `${baseUrl}/?${query}`
}

// Pulled from AD Studio's `manifest.status` block, with the fallback rules
// from the integration guide for older records that don't carry the block yet.
// AD Studio renamed `'published'` → `'active'` (2026-05-05) to avoid colliding
// with the event's own published state. Treat the old value as an alias for
// one release window per their guidance, then drop the alias.
function getProjectStatus (project) {
    const manifest = (project && project.manifest) || {}
    const block = manifest.status || {}
    const descriptionCount = Array.isArray(manifest.audioDescriptions)
        ? manifest.audioDescriptions.length
        : 0
    const rawLifecycle = block.lifecycle || (descriptionCount > 0 ? 'ready' : 'draft')
    return {
        lifecycle: rawLifecycle === 'published' ? 'active' : rawLifecycle,
        processing: block.processing || 'idle',
        error: block.processingError || null,
        startedAt: block.processingStartedAt || null,
        descriptionCount
    }
}

const STATUS_THEME = {
    generating: { color: '#5bc0de', icon: 'fa-spinner fa-spin', label: 'AI generating…' },
    failed:     { color: '#d9534f', icon: 'fa-exclamation-triangle', label: 'Generation failed' },
    active:     { color: '#5cb85c', icon: 'fa-check-circle', label: 'Active' },
    ready:      { color: '#f0ad4e', icon: 'fa-clock-o', label: 'Ready for Review' },
    draft:      { color: '#999', icon: 'fa-pencil', label: 'Draft' }
}

function statusKey (lifecycle, processing) {
    if (processing === 'generating') return 'generating'
    if (processing === 'failed') return 'failed'
    if (lifecycle === 'active') return 'active'
    if (lifecycle === 'ready') return 'ready'
    return 'draft'
}

// Backend timestamps are UTC strings without a zone marker (e.g. "2026-05-04
// 18:19:00"). Parsing with plain moment() treats them as local time, which
// produces "in 7 hours" type bugs in browsers ahead of UTC.
const parseTs = (ts) => moment.utc(ts).local()

function statusSubtext (key, descriptionCount, startedAt) {
    switch (key) {
        case 'generating': return startedAt
            ? `Working for ${parseTs(startedAt).fromNow(true)} — descriptions will appear when ready.`
            : 'Working on it — descriptions will appear when ready.'
        case 'failed':     return 'Generation didn’t finish. Open in AD Studio to retry or take over manually.'
        case 'active':     return `Live in the player. ${descriptionCount} description${descriptionCount === 1 ? '' : 's'} included.`
        case 'ready':      return `${descriptionCount} description${descriptionCount === 1 ? '' : 's'} written. Review and activate in AD Studio.`
        case 'draft':      return descriptionCount > 0
            ? `${descriptionCount} description${descriptionCount === 1 ? '' : 's'} so far.`
            : 'No descriptions yet. Open AD Studio to start writing.'
    }
}

class EventAudioDescription extends Component {
    componentWillMount () {
        const projectID = this.props.event && this.props.event.audioDescriptionProjectID
        if (projectID) {
            this.props.actions.audioDescription.getProject({
                id: projectID,
                clientID: this.props.event.clientID
            })
        }
    }

    render () {
        const { event } = this.props

        // Precondition: AD work is gated on the parent event being published.
        if (!event || event.eventStatus !== 'published') {
            return this.renderEventNotPublished()
        }

        const hasProject = Boolean(event.audioDescriptionProjectID)
        if (hasProject && this.props.adProject.resolving) {
            return <Loader message="Fetching" />
        }

        const project = this.props.adProject.data

        return (
            <div className="col-xs-12">
                <div className="col-sm-9">
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3>Audio Description</h3>
                            <p>Manage the audio description project for this event.</p>
                        </div>

                        <div className="panel-body">
                            {project ? this.renderProject(event, project) : this.renderEmpty()}
                        </div>
                    </div>
                </div>
                <div className="col-sm-3">
                    {this.renderSidebar(event, project)}
                </div>
            </div>
        )
    }

    renderEventNotPublished () {
        return (
            <div className="col-xs-12">
                <div className="col-sm-9">
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3>Audio Description</h3>
                        </div>
                        <div className="panel-body" style={{ textAlign: 'center', padding: '30px 20px' }}>
                            <AudioDescriptionIcon
                                size={56}
                                color="#ccc"
                                style={{ marginBottom: '14px' }}
                            />
                            <p style={{ fontSize: '15px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
                                Audio Description requires the event to be published first
                            </p>
                            <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>
                                Once published, you'll be able to create or review the audio description project here.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    renderProject (event, project) {
        const { lifecycle, processing, error, startedAt, descriptionCount } = getProjectStatus(project)
        const studioUrl = getAdStudioUrl(event, project)
        const key = statusKey(lifecycle, processing)
        const theme = STATUS_THEME[key]

        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '20px',
                        background: '#fafbfc',
                        border: '1px solid #e8eaed',
                        borderLeft: `4px solid ${theme.color}`,
                        borderRadius: '4px',
                        marginBottom: '20px'
                    }}
                >
                    <i
                        className={'fa ' + theme.icon}
                        style={{ fontSize: '32px', color: theme.color, width: '40px', textAlign: 'center' }}
                    />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#222', marginBottom: '2px' }}>
                            {theme.label}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                            {statusSubtext(key, descriptionCount, startedAt)}
                        </div>
                    </div>
                </div>

                {processing === 'failed' && error && (
                    <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
                        <strong>Error:</strong> {error}
                    </div>
                )}

                <a
                    href={studioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                >
                    <i className="fa fa-external-link" style={{ marginRight: '6px' }} />
                    Open in AD Studio
                </a>
            </div>
        )
    }

    renderEmpty () {
        return (
            <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                <AudioDescriptionIcon
                    size={56}
                    color="#ccc"
                    style={{ marginBottom: '14px' }}
                />
                <p style={{ fontSize: '15px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
                    No Audio Description Project
                </p>
                <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>
                    An audio description project will appear here once one is created for this event.
                </p>
            </div>
        )
    }

    renderSidebar (event, project) {
        if (!project) return null

        const { descriptionCount } = getProjectStatus(project)
        const stat = (label, value, sub) => (
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                    {label}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 600, color: '#222', lineHeight: 1.1 }}>{value}</div>
                {sub && <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>{sub}</div>}
            </div>
        )

        return (
            <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: '4px' }}>
                {stat('Descriptions', descriptionCount)}
                {stat(
                    'Updated',
                    parseTs(project.updatedAt).fromNow(),
                    parseTs(project.updatedAt).format('MMM D, YYYY h:mm A')
                )}
            </div>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    const eventID = ownProps.event.eventID
    const adProject = {
        data: state.data.eventAudioDescriptions[eventID] || null,
        resolving: getHasRequestPending(state, ['BUS/http.audioDescription.getProject'])
    }
    return { adProject }
}

export default connect(mapStateToProps)(EventAudioDescription)
